import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const empty = { sku_name: "", sku_code: "", mrp: 0, minimum_selling_price: 0, active_status: true, is_free: false, free_min_order_amount: 0, dp_cost: 0 };

export default function SKUs() {
  const { user } = useAuth();
  const canEdit = user.role === "admin";
  const [skus, setSkus] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(() => api.get("/skus").then(({ data }) => setSkus(data)), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (s) => { setForm(s); setEditing(s.id); setOpen(true); };

  const save = async () => {
    try {
      const payload = {
        ...form,
        mrp: Number(form.mrp),
        minimum_selling_price: Number(form.minimum_selling_price),
        free_min_order_amount: Number(form.free_min_order_amount || 0),
        dp_cost: Number(form.dp_cost || 0),
      };
      if (editing) {
        await api.put(`/skus/${editing}`, payload);
        toast.success("SKU updated");
      } else {
        await api.post("/skus", payload);
        toast.success("SKU created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Inline DP cost editor — commits on blur / Enter. Free products default to 0
  // (users can still store a stub cost if needed) and the profit calc skips them.
  const [dpDraft, setDpDraft] = useState({});   // { sku_id: string }
  const commitDp = async (sku) => {
    const raw = dpDraft[sku.id];
    if (raw === undefined) return;
    const val = Number(raw);
    if (Number.isNaN(val) || val < 0) {
      toast.error("DP cost must be a non-negative number");
      return;
    }
    if (val === Number(sku.dp_cost || 0)) return;   // no change
    try {
      await api.put(`/skus/${sku.id}`, { dp_cost: val });
      toast.success(`DP cost updated for ${sku.sku_code}`);
      setDpDraft((d) => { const c = { ...d }; delete c[sku.id]; return c; });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this SKU?")) return;
    try { await api.delete(`/skus/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = skus.filter((s) =>
    [s.sku_name, s.sku_code].join(" ").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight">SKUs / Products</h1>
          <p className="text-sm text-muted-foreground">Manage products and minimum selling prices</p>
        </div>
        <Button onClick={openNew} disabled={!canEdit} className={canEdit ? "" : "hidden"} data-testid="add-sku-btn"><Plus className="w-4 h-4 mr-2" /> Add SKU</Button>
      </div>

      <Card className="p-4">
        <Input placeholder="Search SKUs..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" data-testid="skus-search" />
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>MRP</TableHead>
                <TableHead>Min. Selling Price</TableHead>
                <TableHead>DP Cost</TableHead>
                <TableHead>Free if order ≥</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No SKUs</TableCell></TableRow>}
              {filtered.map((s) => (
                <TableRow key={s.id} data-testid={`sku-row-${s.sku_code}`}>
                  <TableCell className="font-mono text-xs">{s.sku_code}</TableCell>
                  <TableCell className="font-medium">{s.sku_name}</TableCell>
                  <TableCell>
                    {s.is_free
                      ? <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">Free Product</span>
                      : <span className="text-xs text-muted-foreground">Paid</span>}
                  </TableCell>
                  <TableCell>₹{Number(s.mrp).toFixed(2)}</TableCell>
                  <TableCell>{s.is_free ? "—" : `₹${Number(s.minimum_selling_price).toFixed(2)}`}</TableCell>
                  <TableCell>
                    {canEdit ? (
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        className="h-8 w-24"
                        value={dpDraft[s.id] !== undefined ? dpDraft[s.id] : (s.dp_cost || 0)}
                        onChange={(e) => setDpDraft({ ...dpDraft, [s.id]: e.target.value })}
                        onBlur={() => commitDp(s)}
                        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                        data-testid={`sku-dp-cost-${s.sku_code}`}
                      />
                    ) : (
                      <span className="text-sm">₹{Number(s.dp_cost || 0).toFixed(2)}</span>
                    )}
                  </TableCell>
                  <TableCell>{s.is_free ? `₹${Number(s.free_min_order_amount || 0).toFixed(2)}` : "—"}</TableCell>
                  <TableCell><StatusBadge status={s.active_status ? "active" : "inactive"} /></TableCell>
                  <TableCell className="text-right">
                    {canEdit ? (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-sku-${s.sku_code}`}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => del(s.id)} data-testid={`delete-sku-${s.sku_code}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground">Read-only</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit SKU" : "Add SKU"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>SKU Code</Label>
              <Input value={form.sku_code} onChange={(e) => setForm({ ...form, sku_code: e.target.value })} data-testid="sku-code-input" />
            </div>
            <div className="space-y-2">
              <Label>SKU Name</Label>
              <Input value={form.sku_name} onChange={(e) => setForm({ ...form, sku_name: e.target.value })} data-testid="sku-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>MRP (₹)</Label>
                <Input type="number" step="0.01" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} data-testid="sku-mrp-input" />
              </div>
              {!form.is_free && (
                <div className="space-y-2">
                  <Label>Min. Selling Price (₹)</Label>
                  <Input type="number" step="0.01" value={form.minimum_selling_price} onChange={(e) => setForm({ ...form, minimum_selling_price: e.target.value })} data-testid="sku-min-price-input" />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>DP Cost (₹)</Label>
              <p className="text-xs text-muted-foreground">Distributor Price — cost of this SKU to us. Feeds the profit calculator.</p>
              <Input type="number" step="0.01" min="0" value={form.dp_cost} onChange={(e) => setForm({ ...form, dp_cost: e.target.value })} data-testid="sku-dp-cost-input" />
            </div>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <Label className="font-medium">Free Product</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Given free when order amount meets the threshold below. Max 1 per order.</p>
              </div>
              <Switch checked={form.is_free} onCheckedChange={(v) => setForm({ ...form, is_free: v })} data-testid="sku-free-switch" />
            </div>
            {form.is_free && (
              <div className="space-y-2">
                <Label>Free if order amount ≥ (₹)</Label>
                <Input
                  type="number" step="0.01"
                  value={form.free_min_order_amount}
                  onChange={(e) => setForm({ ...form, free_min_order_amount: e.target.value })}
                  data-testid="sku-free-min-amount"
                />
              </div>
            )}
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={form.active_status} onCheckedChange={(v) => setForm({ ...form, active_status: v })} data-testid="sku-active-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-sku-btn">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
